import React, { useState, Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Sidebar, Header } from "@/components/layout/Layout";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PermissionsProvider, usePermissions } from "@/contexts/PermissionsContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallPrompt, { registerServiceWorker } from "@/components/PWAInstallPrompt";

// Lazy load pages for better performance (code splitting)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ResidentAccounting = lazy(() => import("@/pages/ResidentAccounting"));
const BillingAutomation = lazy(() => import("@/pages/BillingAutomation"));
const Medications = lazy(() => import("@/pages/Medications"));
const CarePlans = lazy(() => import("@/pages/CarePlans"));
const Incidents = lazy(() => import("@/pages/Incidents"));
const Staff = lazy(() => import("@/pages/Staff"));
const Shifts = lazy(() => import("@/pages/Shifts"));
const ShiftSwaps = lazy(() => import("@/pages/ShiftSwaps"));
const ServiceOrders = lazy(() => import("@/pages/ServiceOrders"));
const Settings = lazy(() => import("@/pages/Settings"));
const Login = lazy(() => import("@/pages/Login"));
const Download = lazy(() => import("@/pages/Download"));
const Training = lazy(() => import("@/pages/Training"));
const Apply = lazy(() => import("@/pages/Apply"));
const Applications = lazy(() => import("@/pages/Applications"));
const Communications = lazy(() => import("@/pages/Communications"));
const Profile = lazy(() => import("@/pages/Profile"));
const SignISP = lazy(() => import("@/pages/SignISP"));
const Dietary = lazy(() => import("@/pages/DietaryModule"));
const Meetings = lazy(() => import("@/pages/Meetings"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const HR = lazy(() => import("@/pages/HR"));
const TimeClock = lazy(() => import("@/pages/TimeClock"));
const TAR = lazy(() => import("@/pages/TAR"));
const TimeClockKiosk = lazy(() => import("@/pages/TimeClockKiosk"));
const MobileTimeClock = lazy(() => import("@/pages/MobileTimeClock"));
const CNACharting = lazy(() => import("@/pages/CNACharting"));
const ChartingAnalytics = lazy(() => import("@/pages/ChartingAnalytics"));
const KioskQRCodes = lazy(() => import("@/pages/KioskQRCodes"));
const CaregiverCarePlan = lazy(() => import("@/pages/CaregiverCarePlan"));
const CaregiverCarePlanDashboard = lazy(() => import("@/pages/CaregiverCarePlanDashboard"));
const DHS83ComplianceDashboard = lazy(() => import("@/pages/DHS83ComplianceDashboard"));
const DHS83AssessmentForm = lazy(() => import("@/pages/DHS83AssessmentForm"));
const DHS83ISPForm = lazy(() => import("@/pages/DHS83ISPForm"));
const ComplianceReminders = lazy(() => import("@/pages/ComplianceReminders"));
const PTORequests = lazy(() => import("@/pages/PTORequests"));
const ReportingDashboard = lazy(() => import("@/pages/ReportingDashboard"));
const TrainingDocumentation = lazy(() => import("@/pages/TrainingDocumentation"));
const CLTSDashboard = lazy(() => import("@/pages/CLTSDashboard"));
const CLTSParticipants = lazy(() => import("@/pages/CLTSParticipants"));
const CLTSContactLog = lazy(() => import("@/pages/CLTSContactLog"));
const ContactComplianceDashboard = lazy(() => import("@/pages/ContactComplianceDashboard"));
const CLTSVisits = lazy(() => import("@/pages/CLTSVisits"));
const CLTSIntakeForms = lazy(() => import("@/pages/CLTSIntakeForms"));
const CLTSSchedule = lazy(() => import("@/pages/CLTSSchedule"));
const CLTSStaff = lazy(() => import("@/pages/CLTSStaff"));
const CLTSRespiteForms = lazy(() => import("@/pages/CLTSRespiteForms"));
const CLTSRespiteCare = lazy(() => import("@/pages/CLTSRespiteCare"));
const CLTSISP = lazy(() => import("@/pages/CLTSISP"));
const Transportation = lazy(() => import("@/pages/Transportation"));
const TransportationLog = lazy(() => import("@/pages/TransportationLog"));
const MentoringVisit = lazy(() => import("@/pages/MentoringVisit"));
const FamilyIntakeForm = lazy(() => import("@/pages/FamilyIntakeForm"));
const PublicIntakeForm = lazy(() => import("@/pages/PublicIntakeForm"));
const ReferralIntakeForm = lazy(() => import("@/pages/ReferralIntakeForm"));
const AdmissionForms = lazy(() => import("@/pages/AdmissionForms"));
const PublicAdmissionForms = lazy(() => import("@/pages/PublicAdmissionForms"));

// Portal pages
const CaseManagerPortal = lazy(() => import("@/pages/CaseManagerPortal"));
const SubscriptionManagement = lazy(() => import("@/pages/SubscriptionManagement"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const CaseManagerInvites = lazy(() => import("@/pages/CaseManagerInvites"));
const Register = lazy(() => import("@/pages/Register"));
const OrganizationSettings = lazy(() => import("@/pages/OrganizationSettings"));
const Billing = lazy(() => import("@/pages/Billing"));

// Named exports need special handling for lazy loading
const ResidentsList = lazy(() => import("@/pages/Residents").then(module => ({ default: module.ResidentsList })));
const ResidentDetail = lazy(() => import("@/pages/Residents").then(module => ({ default: module.ResidentDetail })));

// Loading spinner for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Protected Route wrapper with permission check
function ProtectedRoute({ children, allowedRoles, module, action, redirectRoles }) {
  const { isAuthenticated, loading, hasRole, user } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  
  if (loading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Role-based redirect (e.g., DSP users to CLTS dashboard)
  if (redirectRoles && user?.role && redirectRoles[user.role]) {
    return <Navigate to={redirectRoles[user.role]} replace />;
  }
  
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/" replace />;
  }

  // Check module permission if specified
  if (module && action && !hasPermission(module, action)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Restricted</h2>
          <p className="text-slate-500">You don&apos;t have permission to view this page.</p>
          <p className="text-sm text-slate-400 mt-2">Contact your administrator for access.</p>
        </div>
      </div>
    );
  }
  
  return children;
}

// Layout wrapper component
function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return children;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 lg:flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/reset-password" element={<Login />} />
          <Route path="/sign-isp/:token" element={<SignISP />} />
          <Route path="/portal/accept-invite" element={<AcceptInvite />} />
          <Route path="/family/intake/:token" element={<FamilyIntakeForm />} />
          <Route path="/intake/referral/:token" element={<ReferralIntakeForm />} />
          <Route path="/admission/forms/:token" element={<PublicAdmissionForms />} />
          <Route path="/intake/:orgSlug" element={<PublicIntakeForm />} />
          
          {/* Case Manager Portal (restricted to case_manager role) */}
          <Route path="/portal/case-manager" element={
            <ProtectedRoute allowedRoles={['case_manager']}>
              <CaseManagerPortal />
            </ProtectedRoute>
          } />
          
          {/* Subscription Management */}
          <Route path="/portal/subscription" element={
            <ProtectedRoute allowedRoles={['admin', 'subscriber']}>
              <SubscriptionManagement />
            </ProtectedRoute>
          } />
          
          {/* Case Manager Invites (admin only) */}
          <Route path="/portal/case-manager-invites" element={
            <ProtectedRoute allowedRoles={['admin', 'supervisor', 'hr']}>
              <CaseManagerInvites />
            </ProtectedRoute>
          } />
          
          {/* Protected routes with permission checks */}
          <Route path="/" element={
            <ProtectedRoute 
              module="dashboard" 
              action="view"
              redirectRoles={{ dsp: '/clts', driver: '/clts', case_manager: '/portal/case-manager' }}
            >
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/residents" element={
            <ProtectedRoute module="residents" action="view">
              <ResidentsList />
            </ProtectedRoute>
          } />
          <Route path="/residents/clts-respite" element={
            <ProtectedRoute module="residents" action="view">
              <CLTSRespiteCare />
            </ProtectedRoute>
          } />
          <Route path="/transportation" element={
            <Transportation />
          } />
          <Route path="/residents/:id" element={
            <ProtectedRoute module="residents" action="view">
              <ResidentDetail />
            </ProtectedRoute>
          } />
          <Route path="/resident-accounting" element={
            <ProtectedRoute module="accounting" action="view">
              <ResidentAccounting />
          </ProtectedRoute>
        } />
        <Route path="/billing-automation" element={
          <ProtectedRoute module="accounting" action="view">
            <BillingAutomation />
          </ProtectedRoute>
        } />
        <Route path="/medications" element={
          <ProtectedRoute module="medications" action="view">
            <Medications />
          </ProtectedRoute>
        } />
        <Route path="/tar" element={
          <ProtectedRoute module="medications" action="view">
            <TAR />
          </ProtectedRoute>
        } />
        <Route path="/care-plans" element={
          <ProtectedRoute module="care_plans" action="view">
            <CarePlans />
          </ProtectedRoute>
        } />
        <Route path="/caregiver-care-plans" element={
          <ProtectedRoute module="care_plans" action="view">
            <CaregiverCarePlanDashboard />
          </ProtectedRoute>
        } />
        <Route path="/caregiver-care-plan/:residentId" element={
          <ProtectedRoute module="care_plans" action="view">
            <CaregiverCarePlan />
          </ProtectedRoute>
        } />
        <Route path="/incidents" element={
          <ProtectedRoute module="incidents" action="view">
            <Incidents />
          </ProtectedRoute>
        } />
        <Route path="/staff" element={
          <ProtectedRoute module="staff" action="view">
            <Staff />
          </ProtectedRoute>
        } />
        <Route path="/shifts" element={
          <ProtectedRoute module="shifts" action="view">
            <Shifts />
          </ProtectedRoute>
        } />
        <Route path="/shift-swaps" element={
          <ProtectedRoute module="shifts" action="view">
            <ShiftSwaps />
          </ProtectedRoute>
        } />
        <Route path="/service-orders" element={
          <ProtectedRoute module="maintenance" action="view">
            <ServiceOrders />
          </ProtectedRoute>
        } />
        <Route path="/training" element={
          <ProtectedRoute module="training" action="view">
            <Training />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute module="settings" action="view">
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/applications" element={
          <ProtectedRoute module="applications" action="view">
            <Applications />
          </ProtectedRoute>
        } />
        <Route path="/communications" element={
          <ProtectedRoute module="communications" action="view">
            <Communications />
          </ProtectedRoute>
        } />
        <Route path="/dietary" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/dietary/menu-planner" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/dietary/production" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/dietary/tray-service" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/dietary/scheduling" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/dietary/reports" element={
          <ProtectedRoute module="dietary" action="view">
            <Dietary />
          </ProtectedRoute>
        } />
        <Route path="/meetings" element={
          <ProtectedRoute>
            <Meetings />
          </ProtectedRoute>
        } />
        <Route path="/payroll" element={
          <ProtectedRoute module="payroll" action="view">
            <Payroll />
          </ProtectedRoute>
        } />
        <Route path="/hr" element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'supervisor']}>
            <HR />
          </ProtectedRoute>
        } />
        <Route path="/timeclock" element={
          <ProtectedRoute module="time_clock" action="view">
            <TimeClock />
          </ProtectedRoute>
        } />
        <Route path="/timeclock/kiosk" element={<TimeClockKiosk />} />
        <Route path="/timeclock/mobile" element={
          <ProtectedRoute>
            <MobileTimeClock />
          </ProtectedRoute>
        } />
        <Route path="/timeclock/qr-codes" element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'supervisor']}>
            <KioskQRCodes />
          </ProtectedRoute>
        } />
        <Route path="/punch" element={<MobileTimeClock />} />
        <Route path="/hr-templates" element={<Navigate to="/hr" replace />} />
        <Route path="/hr-management" element={<Navigate to="/hr" replace />} />
        <Route path="/charting" element={
          <ProtectedRoute module="charting" action="view">
            <CNACharting />
          </ProtectedRoute>
        } />
        <Route path="/charting/analytics" element={
          <ProtectedRoute module="charting" action="view">
            <ChartingAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/dhs83" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse']}>
            <DHS83ComplianceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/dhs83/assessment/:residentId" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse']}>
            <DHS83AssessmentForm />
          </ProtectedRoute>
        } />
        <Route path="/dhs83/isp/new" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse']}>
            <DHS83ISPForm />
          </ProtectedRoute>
        } />
        <Route path="/compliance-reminders" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'don', 'hr', 'rn', 'lpn', 'nurse']}>
            <ComplianceReminders />
          </ProtectedRoute>
        } />
        <Route path="/pto-requests" element={
          <ProtectedRoute>
            <PTORequests />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'don', 'hr']}>
            <ReportingDashboard />
          </ProtectedRoute>
        } />
        <Route path="/training-docs" element={
          <ProtectedRoute>
            <TrainingDocumentation />
          </ProtectedRoute>
        } />
        
        {/* CLTS Case Management Routes */}
        <Route path="/clts" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSDashboard />
          </ProtectedRoute>
        } />
        <Route path="/clts/participants" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSParticipants />
          </ProtectedRoute>
        } />
        <Route path="/clts/participants/new" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSParticipants />
          </ProtectedRoute>
        } />
        <Route path="/clts/contacts" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSContactLog />
          </ProtectedRoute>
        } />
        <Route path="/clts/contacts/new" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSContactLog />
          </ProtectedRoute>
        } />
        <Route path="/clts/contact-compliance" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr']}>
            <ContactComplianceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/clts/visits" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSVisits />
          </ProtectedRoute>
        } />
        <Route path="/clts/visits/new" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSVisits />
          </ProtectedRoute>
        } />
        <Route path="/clts/schedule" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSSchedule />
          </ProtectedRoute>
        } />
        <Route path="/clts/staff" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr']}>
            <CLTSStaff />
          </ProtectedRoute>
        } />
        <Route path="/clts/intake" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSIntakeForms />
          </ProtectedRoute>
        } />
        <Route path="/clts/intake/new" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSIntakeForms />
          </ProtectedRoute>
        } />
        <Route path="/clts/respite" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSRespiteForms />
          </ProtectedRoute>
        } />
        <Route path="/clts/isp" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <CLTSISP />
          </ProtectedRoute>
        } />
        <Route path="/clts/admission" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <AdmissionForms />
          </ProtectedRoute>
        } />
        <Route path="/clts/transportation-log" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <TransportationLog />
          </ProtectedRoute>
        } />
        <Route path="/clts/mentoring" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor', 'nurse', 'don', 'hr', 'dsp', 'driver']}>
            <MentoringVisit />
          </ProtectedRoute>
        } />
        
        <Route path="/billing" element={
          <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
            <Billing />
          </ProtectedRoute>
        } />
        
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        
        <Route path="/organization-settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <OrganizationSettings />
          </ProtectedRoute>
        } />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

function App() {
  // Register service worker on app load
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PermissionsProvider>
            <NotificationProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes - no auth required */}
                  <Route path="/apply" element={<Apply />} />
                  
                  {/* Download page - standalone, no layout */}
                  <Route path="/download" element={
                    <ProtectedRoute>
                      <Download />
                    </ProtectedRoute>
                  } />
                  {/* All other routes with layout */}
                  <Route path="/*" element={
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                  } />
                </Routes>
              </Suspense>
              <Toaster position="top-right" richColors />
              <PWAInstallPrompt />
            </NotificationProvider>
          </PermissionsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
